import {
  IntegrationDefinition,
  IntegrationConnection,
  CreateIntegrationConnectionRequest,
  UpdateIntegrationConnectionRequest,
  OAuthUrlResponse,
  TestConnectionResult,
  OperationSchemaResponse,
} from "@/types/breeze/Integration";
import { BaseService } from "./BaseService";

/** Shape returned by both the per-provider and bulk OAuth-settings endpoints. */
export interface IntegrationOAuthStatus {
  native_audiences: string[];
  web_client_id?: string | null;
  has_web_client_secret: boolean;
  has_apple_private_key: boolean;
  apple_key_id?: string | null;
  apple_team_id?: string | null;
  allow_missing_email: boolean;
  native_flow_configured: boolean;
  web_flow_configured: boolean;
  use_social_sign_in: boolean;
  is_enabled: boolean;
  has_client_id: boolean;
}

class IntegrationService extends BaseService {
  async getDefinitions(): Promise<IntegrationDefinition[]> {
    return this.getRequest<IntegrationDefinition[]>(
      `/integrations/definitions`,
      true,
    );
  }

  async getDefinition(provider: string): Promise<IntegrationDefinition> {
    return this.getRequest<IntegrationDefinition>(
      `/integrations/definitions/${provider}`,
      true,
    );
  }

  async getConnections(): Promise<IntegrationConnection[]> {
    return this.getRequest<IntegrationConnection[]>(
      `/integrations/connections`,
      true,
    );
  }

  async getConnectionsForProvider(
    provider: string,
  ): Promise<IntegrationConnection[]> {
    return this.getRequest<IntegrationConnection[]>(
      `/integrations/definitions/${provider}/connections`,
      true,
    );
  }

  async createConnection(
    request: CreateIntegrationConnectionRequest,
  ): Promise<IntegrationConnection> {
    return this.postRequest<
      CreateIntegrationConnectionRequest,
      IntegrationConnection
    >(`/integrations/connections`, request, "Connection created successfully");
  }

  async updateConnection(
    connectionId: string,
    request: UpdateIntegrationConnectionRequest,
  ): Promise<IntegrationConnection> {
    return this.putRequest<
      UpdateIntegrationConnectionRequest,
      IntegrationConnection
    >(
      `/integrations/connections/${connectionId}`,
      request,
      "Connection updated successfully",
    );
  }

  async createApiKeyConnection(request: { integration_definition_id: string; name: string; api_key: string; is_user_connection: boolean }): Promise<IntegrationConnection> {
    return this.postRequest<typeof request, IntegrationConnection>(
      `/integrations/connections/api-key`, request, "API key saved successfully"
    );
  }

  async deleteConnection(connectionId: string): Promise<void> {
    return this.deleteRequest(
      `/integrations/connections/${connectionId}`,
      "Connection deleted successfully",
    );
  }

  async testConnection(connectionId: string): Promise<TestConnectionResult> {
    return this.postRequest<object, TestConnectionResult>(
      `/integrations/connections/${connectionId}/test`,
      {},
      undefined,
      true,
      true,
    );
  }

  async getOAuthUrl(provider: string, redirectUri: string): Promise<string> {
    const params = new URLSearchParams({ redirectUri });
    const response = await this.getRequest<OAuthUrlResponse>(
      `/integrations/definitions/${provider}/oauth-url?${params.toString()}`,
      true,
    );
    return response.url;
  }

  async getOperationSchema(
    provider: string,
    operation: string,
  ): Promise<OperationSchemaResponse> {
    return this.getRequest<OperationSchemaResponse>(
      `/integrations/definitions/${provider}/operations/${operation}/schema`,
      true,
    );
  }

  async getIntegrationOAuthSettings(provider: string): Promise<IntegrationOAuthStatus> {
    return this.getRequest(`/integrations/definitions/${provider}/oauth`, true);
  }

  /**
   * Bulk version of getIntegrationOAuthSettings. Returns status for every
   * definition the tenant can see, keyed by provider id. Used by the
   * Integrations page so it can render configured/off pills in one network
   * round-trip instead of N sequential ones.
   */
  async getAllIntegrationOAuthSettings(): Promise<
    Record<string, IntegrationOAuthStatus>
  > {
    return this.getRequest(`/integrations/definitions/oauth-all`, true);
  }

  async setIntegrationOAuthSettings(
    provider: string,
    settings: {
      native_audiences?: string[];
      web_client_id?: string;
      web_client_secret?: string;
      apple_private_key?: string;
      apple_key_id?: string;
      apple_team_id?: string;
      allow_missing_email?: boolean;
      is_enabled?: boolean;
      use_social_sign_in: boolean;
    },
  ): Promise<void> {
    return this.putRequest(
      `/integrations/definitions/${provider}/oauth`,
      settings,
      "Settings updated successfully",
      true,
      true,
    );
  }

  async getFieldOptions(
    provider: string,
    field: string,
  ): Promise<Array<{ value: string; label: string }>> {
    return this.getRequest<Array<{ value: string; label: string }>>(
      `/integrations/definitions/${provider}/fields/${field}/options`,
      true,
    );
  }
}

export const integrationService = new IntegrationService();
